import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { usePermission } from '@/shell/module-system'
import { ArrowLeft, CheckCircle2, Download, FileText, Loader2, RotateCcw, Save, Send, Trash2 } from 'lucide-react'
import {
  Button,
  Checkbox,
  Input,
  QueryErrorState,
  Textarea,
  toast,
} from '@/components/ui'
import {
  useCreateDiscrepancy,
  useDeleteDiscrepancy,
  useDiscrepancy,
  useGenerateDiscrepancyPdf,
  useLinkIncidentReport,
  useUnlinkIncidentReport,
  useUpdateDiscrepancy,
} from '../api/queries'
import type {
  CorrectiveAction,
  DiscrepancyReport,
  DiscrepancyType,
  TimelineEvent,
} from '../api/types'
import { DISCREPANCY_TYPE_LABELS } from '../api/types'
import { DiscrepancyStatusChip } from '../components/DiscrepancyStatusChip'
import { TimelineEventsEditor } from '../components/TimelineEventsEditor'
import { CorrectiveActionsEditor } from '../components/CorrectiveActionsEditor'

// ─── Section wrapper ────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1A3A5C] text-white text-sm font-bold px-3 py-2 rounded-t">
      {label}
    </div>
  )
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-border-default border-t-0 rounded-b p-4 space-y-4">
      {children}
    </div>
  )
}

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{children}</div>
}

function Field({
  label,
  children,
  colSpan = 1,
}: {
  label: string
  children: React.ReactNode
  colSpan?: 1 | 2 | 3
}) {
  const spanClass = colSpan === 3 ? 'sm:col-span-3' : colSpan === 2 ? 'sm:col-span-2' : ''
  return (
    <div className={`space-y-1 ${spanClass}`}>
      <label className="text-xs text-secondary uppercase tracking-wide font-medium">
        {label}
      </label>
      {children}
    </div>
  )
}

// ─── Form state ─────────────────────────────────────────────────────

interface FormState {
  report_date: string
  prepared_by: string
  prepared_for: string
  date_discovered: string
  date_of_occurrence: string
  days_unresolved: string
  facility_site: string
  discrepancy_types: DiscrepancyType[]
  purchase_order: string
  vendor: string
  product: string
  quantity: string
  scheduled_receipt_date: string
  odoo_receipt: string
  current_record_status: string
  po_buyer: string
  narrative: string
  business_impact: string
  initial_cause: string
  recommended_followup: string
  timeline_events: TimelineEvent[]
  corrective_actions: CorrectiveAction[]
}

function blankForm(): FormState {
  return {
    report_date: new Date().toISOString().slice(0, 10),
    prepared_by: '',
    prepared_for: '',
    date_discovered: '',
    date_of_occurrence: '',
    days_unresolved: '',
    facility_site: '',
    discrepancy_types: [],
    purchase_order: '',
    vendor: '',
    product: '',
    quantity: '',
    scheduled_receipt_date: '',
    odoo_receipt: '',
    current_record_status: '',
    po_buyer: '',
    narrative: '',
    business_impact: '',
    initial_cause: '',
    recommended_followup: '',
    timeline_events: [{ when: '', event: '', result: '' }],
    corrective_actions: [{ action: '', owner: '', due_date: '' }],
  }
}

function reportToForm(r: DiscrepancyReport): FormState {
  return {
    report_date: r.report_date ?? '',
    prepared_by: r.prepared_by ?? '',
    prepared_for: r.prepared_for ?? '',
    date_discovered: r.date_discovered ?? '',
    date_of_occurrence: r.date_of_occurrence ?? '',
    days_unresolved: r.days_unresolved != null ? String(r.days_unresolved) : '',
    facility_site: r.facility_site ?? '',
    discrepancy_types: r.discrepancy_types ?? [],
    purchase_order: r.purchase_order ?? '',
    vendor: r.vendor ?? '',
    product: r.product ?? '',
    quantity: r.quantity ?? '',
    scheduled_receipt_date: r.scheduled_receipt_date ?? '',
    odoo_receipt: r.odoo_receipt ?? '',
    current_record_status: r.current_record_status ?? '',
    po_buyer: r.po_buyer ?? '',
    narrative: r.narrative ?? '',
    business_impact: r.business_impact ?? '',
    initial_cause: r.initial_cause ?? '',
    recommended_followup: r.recommended_followup ?? '',
    timeline_events: r.timeline_events.length > 0 ? r.timeline_events : [{ when: '', event: '', result: '' }],
    corrective_actions: r.corrective_actions.length > 0 ? r.corrective_actions : [{ action: '', owner: '', due_date: '' }],
  }
}

// ─── Page ───────────────────────────────────────────────────────────

export function InventoryDiscrepancyReportPage() {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { reportId?: string }
  const reportId = params.reportId === 'new' ? null : (params.reportId ?? null)
  const isNew = reportId === null

  const { data: report, isLoading, isError, error, refetch } = useDiscrepancy(reportId)
  const createMutation = useCreateDiscrepancy()
  const updateMutation = useUpdateDiscrepancy(reportId ?? '')
  const deleteMutation = useDeleteDiscrepancy()
  const pdfMutation = useGenerateDiscrepancyPdf(reportId ?? '')

  const [form, setForm] = useState<FormState>(blankForm)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (report) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs fetched report into form state; gated by report identity so no loop.
      setForm(reportToForm(report))
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears dirty flag when remote data arrives; paired with the setForm above.
      setDirty(false)
    }
  }, [report])

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }))
    setDirty(true)
  }

  function toggleType(type: DiscrepancyType) {
    const current = form.discrepancy_types
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type]
    set('discrepancy_types', next)
  }

  function buildPayload() {
    const days = form.days_unresolved.trim()
    return {
      report_date: form.report_date || new Date().toISOString().slice(0, 10),
      prepared_by: form.prepared_by,
      prepared_for: form.prepared_for,
      date_discovered: form.date_discovered || undefined,
      date_of_occurrence: form.date_of_occurrence || undefined,
      days_unresolved: days === '' ? undefined : Number(days),
      facility_site: form.facility_site,
      discrepancy_types: form.discrepancy_types,
      purchase_order: form.purchase_order,
      vendor: form.vendor,
      product: form.product,
      quantity: form.quantity,
      scheduled_receipt_date: form.scheduled_receipt_date || undefined,
      odoo_receipt: form.odoo_receipt,
      current_record_status: form.current_record_status,
      po_buyer: form.po_buyer,
      narrative: form.narrative,
      business_impact: form.business_impact,
      initial_cause: form.initial_cause,
      recommended_followup: form.recommended_followup,
      timeline_events: form.timeline_events.filter((t) => t.when || t.event || t.result),
      corrective_actions: form.corrective_actions.filter((a) => a.action || a.owner || a.due_date),
    }
  }

  async function handleSave() {
    try {
      const payload = buildPayload()
      if (isNew) {
        const created = await createMutation.mutateAsync(payload)
        toast.success(`Report ${created.report_number} created.`)
        navigate({ to: '/inventory-discrepancy-reports/$reportId', params: { reportId: created.id } })
      } else {
        await updateMutation.mutateAsync(payload)
        toast.success('Report saved.')
        setDirty(false)
      }
    } catch {
      toast.danger('Save failed. Please try again.')
    }
  }

  async function handleAction(action: 'open' | 'resolve' | 'reopen', successMsg: string) {
    try {
      if (dirty) await handleSave()
      await updateMutation.mutateAsync({ action })
      toast.success(successMsg)
    } catch {
      toast.danger('Action failed.')
    }
  }

  async function handleGeneratePdf() {
    try {
      const result = await pdfMutation.mutateAsync()
      window.open(`/api/exports/${result.file_id}`, '_blank')
    } catch {
      toast.danger('PDF generation failed.')
    }
  }

  async function handleDelete() {
    if (!reportId) return
    if (!confirm('Delete this discrepancy report? This cannot be undone.')) return
    try {
      await deleteMutation.mutateAsync(reportId)
      toast.success('Report deleted.')
      navigate({ to: '/inventory-discrepancy-reports' })
    } catch {
      toast.danger('Delete failed.')
    }
  }

  const isAdmin = usePermission('admin')
  const isSaving = createMutation.isPending || updateMutation.isPending
  const isReadOnly = !isNew && report?.status === 'resolved' && !isAdmin
  const canDelete = !isNew && (report?.status === 'draft' || isAdmin)

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-secondary" size={24} />
      </div>
    )
  }

  if (!isNew && isError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <QueryErrorState
          error={error}
          onRetry={() => void refetch()}
          subject="this discrepancy report"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Topbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default bg-surface sticky top-0 z-10">
        <button
          type="button"
          onClick={() => navigate({ to: '/inventory-discrepancy-reports' })}
          className="text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">
              {isNew ? 'New Discrepancy Report' : (report?.report_number ?? 'Discrepancy Report')}
            </h1>
            {report && <DiscrepancyStatusChip status={report.status} />}
          </div>
          {report && (
            <p className="text-xs text-secondary mt-0.5">
              Created by {report.created_by ?? 'unknown'} on{' '}
              {report.created_at ? new Date(report.created_at).toLocaleDateString() : '—'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {report?.pdf_file_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/api/exports/${report.pdf_file_id}`, '_blank')}
            >
              <Download size={14} className="mr-1" />
              PDF
            </Button>
          )}
          {!isNew && !isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGeneratePdf}
              loading={pdfMutation.isPending}
            >
              <FileText size={14} className="mr-1" />
              {report?.pdf_file_id ? 'Regenerate PDF' : 'Generate PDF'}
            </Button>
          )}
          {!isReadOnly && dirty && (
            <Button variant="secondary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save
            </Button>
          )}
          {!isNew && report?.status === 'draft' && (
            <Button variant="primary" size="sm" onClick={() => handleAction('open', 'Report opened.')} loading={isSaving}>
              <Send size={14} className="mr-1" />
              Open
            </Button>
          )}
          {!isNew && report?.status === 'open' && (
            <Button variant="primary" size="sm" onClick={() => handleAction('resolve', 'Report resolved.')} loading={isSaving}>
              <CheckCircle2 size={14} className="mr-1" />
              Resolve
            </Button>
          )}
          {!isNew && report?.status === 'resolved' && isAdmin && (
            <Button variant="secondary" size="sm" onClick={() => handleAction('reopen', 'Report reopened.')} loading={isSaving}>
              <RotateCcw size={14} className="mr-1" />
              Reopen
            </Button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-secondary hover:text-danger transition-colors ml-1"
              title="Delete report"
            >
              <Trash2 size={16} />
            </button>
          )}
          {isNew && (
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              Create Report
            </Button>
          )}
        </div>
      </div>

      {/* Form body */}
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6 space-y-6">
        {/* Report details */}
        <div>
          <SectionHeader label="REPORT DETAILS" />
          <SectionBody>
            <FieldGroup>
              {!isNew && (
                <Field label="Report Number">
                  <Input value={report?.report_number ?? ''} disabled />
                </Field>
              )}
              <Field label="Report Date">
                <Input
                  type="date"
                  value={form.report_date}
                  onChange={(e) => set('report_date', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Facility / Site" colSpan={isNew ? 2 : 1}>
                <Input
                  value={form.facility_site}
                  onChange={(e) => set('facility_site', e.target.value)}
                  placeholder="e.g. SPIWH"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Prepared By">
                <Input
                  value={form.prepared_by}
                  onChange={(e) => set('prepared_by', e.target.value)}
                  placeholder="Name of preparer"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Prepared For">
                <Input
                  value={form.prepared_for}
                  onChange={(e) => set('prepared_for', e.target.value)}
                  placeholder="Recipient"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Days Unresolved">
                <Input
                  type="number"
                  min={0}
                  value={form.days_unresolved}
                  onChange={(e) => set('days_unresolved', e.target.value)}
                  placeholder="0"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Date of Occurrence">
                <Input
                  type="date"
                  value={form.date_of_occurrence}
                  onChange={(e) => set('date_of_occurrence', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Date Discovered">
                <Input
                  type="date"
                  value={form.date_discovered}
                  onChange={(e) => set('date_discovered', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <div className="space-y-2">
              <label className="text-xs text-secondary uppercase tracking-wide font-medium">
                Discrepancy Type (check all that apply)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(Object.entries(DISCREPANCY_TYPE_LABELS) as [DiscrepancyType, string][]).map(
                  ([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={form.discrepancy_types.includes(key)}
                        onChange={() => toggleType(key)}
                        disabled={isReadOnly}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  )
                )}
              </div>
            </div>
          </SectionBody>
        </div>

        {/* Reference / shipment information */}
        <div>
          <SectionHeader label="REFERENCE / SHIPMENT INFORMATION" />
          <SectionBody>
            <FieldGroup>
              <Field label="Purchase Order">
                <Input value={form.purchase_order} onChange={(e) => set('purchase_order', e.target.value)} placeholder="e.g. P01853" disabled={isReadOnly} />
              </Field>
              <Field label="Vendor" colSpan={2}>
                <Input value={form.vendor} onChange={(e) => set('vendor', e.target.value)} placeholder="Vendor name" disabled={isReadOnly} />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Product" colSpan={2}>
                <Input value={form.product} onChange={(e) => set('product', e.target.value)} placeholder="Product code / name" disabled={isReadOnly} />
              </Field>
              <Field label="Quantity">
                <Input value={form.quantity} onChange={(e) => set('quantity', e.target.value)} placeholder="e.g. 1,800 kg" disabled={isReadOnly} />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field label="Scheduled Receipt Date">
                <Input type="date" value={form.scheduled_receipt_date} onChange={(e) => set('scheduled_receipt_date', e.target.value)} disabled={isReadOnly} />
              </Field>
              <Field label="Odoo Receipt">
                <Input value={form.odoo_receipt} onChange={(e) => set('odoo_receipt', e.target.value)} placeholder="e.g. SPIWH/IN/01997" disabled={isReadOnly} />
              </Field>
              <Field label="PO Buyer">
                <Input value={form.po_buyer} onChange={(e) => set('po_buyer', e.target.value)} placeholder="Buyer name" disabled={isReadOnly} />
              </Field>
            </FieldGroup>
            <Field label="Current Status (of the Odoo record)" colSpan={3}>
              <Input value={form.current_record_status} onChange={(e) => set('current_record_status', e.target.value)} placeholder="e.g. Open / Not Validated" disabled={isReadOnly} />
            </Field>
          </SectionBody>
        </div>

        {/* Narrative */}
        <div>
          <SectionHeader label="INCIDENT NARRATIVE" />
          <SectionBody>
            <Field label="Describe What Happened" colSpan={3}>
              <Textarea
                value={form.narrative}
                onChange={(e) => set('narrative', e.target.value)}
                placeholder="Describe the discrepancy and the sequence of events…"
                rows={5}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Timeline of Events */}
        <div>
          <SectionHeader label="TIMELINE OF EVENTS" />
          <SectionBody>
            <TimelineEventsEditor
              items={form.timeline_events}
              onChange={(items) => set('timeline_events', items)}
              readOnly={isReadOnly}
            />
          </SectionBody>
        </div>

        {/* Business Impact */}
        <div>
          <SectionHeader label="BUSINESS IMPACT" />
          <SectionBody>
            <Field label="Impact to Operations" colSpan={3}>
              <Textarea
                value={form.business_impact}
                onChange={(e) => set('business_impact', e.target.value)}
                placeholder="How this affects inventory accuracy, fulfillment, purchasing…"
                rows={4}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Initial Cause / Process Gap */}
        <div>
          <SectionHeader label="INITIAL CAUSE / PROCESS GAP" />
          <SectionBody>
            <Field label="Root Cause / Process Gap" colSpan={3}>
              <Textarea
                value={form.initial_cause}
                onChange={(e) => set('initial_cause', e.target.value)}
                placeholder="Immediate issue and the larger process gap…"
                rows={3}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Corrective & Preventive Actions */}
        <div>
          <SectionHeader label="CORRECTIVE & PREVENTIVE ACTIONS" />
          <SectionBody>
            <CorrectiveActionsEditor
              items={form.corrective_actions}
              onChange={(items) => set('corrective_actions', items)}
              readOnly={isReadOnly}
            />
          </SectionBody>
        </div>

        {/* Recommended Follow-Up */}
        <div>
          <SectionHeader label="RECOMMENDED FOLLOW-UP" />
          <SectionBody>
            <Field label="Follow-Up Items" colSpan={3}>
              <Textarea
                value={form.recommended_followup}
                onChange={(e) => set('recommended_followup', e.target.value)}
                placeholder="Recommended verification and follow-up steps…"
                rows={3}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Linked Records */}
        {!isNew && report && (
          <div>
            <SectionHeader label="LINKED RECORDS" />
            <SectionBody>
              <LinkedIncidentPanel report={report} isReadOnly={isReadOnly} />
            </SectionBody>
          </div>
        )}

        {/* Bottom save bar */}
        {!isReadOnly && dirty && (
          <div className="flex justify-end py-4">
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Linked records (cross-module: Warehouse Incident Reports) ─────

function LinkedIncidentPanel({
  report,
  isReadOnly,
}: {
  report: DiscrepancyReport
  isReadOnly: boolean
}) {
  const [draftId, setDraftId] = useState('')
  const link = useLinkIncidentReport(report.id)
  const unlink = useUnlinkIncidentReport(report.id)

  if (report.warehouse_incident_id) {
    return (
      <div className="flex items-center gap-3">
        <a
          href={`/warehouse-incidents/${report.warehouse_incident_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-link hover:underline"
        >
          View linked Incident Report ↗
        </a>
        {!isReadOnly && (
          <Button
            variant="ghost"
            size="sm"
            loading={unlink.isPending}
            onClick={() =>
              unlink.mutate(undefined, {
                onSuccess: () => toast.success('Unlinked'),
                onError: (err) =>
                  toast.danger(`Unlink failed${err instanceof Error ? `: ${err.message}` : ''}`),
              })
            }
          >
            Unlink
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={draftId}
        onChange={(e) => setDraftId(e.target.value)}
        placeholder="Incident Report ID (UUID)"
        disabled={isReadOnly}
        className="max-w-xs"
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={isReadOnly || !draftId.trim()}
        loading={link.isPending}
        onClick={() =>
          link.mutate(draftId.trim(), {
            onSuccess: () => {
              toast.success('Linked')
              setDraftId('')
            },
            onError: (err) =>
              toast.danger(`Link failed${err instanceof Error ? `: ${err.message}` : ''}`),
          })
        }
      >
        Link
      </Button>
    </div>
  )
}
